import React, { useEffect, useState } from "react";
import { useUserPermissions } from "../../../hooks/useUserPermissions";
import { userApi } from "../../../repositories/user";
import { handleError } from "../../../utils/errorHandler";
import { format } from "date-fns";
import SettingsPanel from "../../ui/SettingsPanel";
import Button from "../../ui/Button";

interface SubscriptionData {
  status: string;
  current_period_start: string;
  current_period_end: string;
  package_id: string;
  stripe_price: string;
  quantity: number;
  plan_name: string;
}

const SubscriptionDetails: React.FC = () => {
  const { permissions } = useUserPermissions();
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isRedirecting, setIsRedirecting] = useState<boolean>(false);

  useEffect(() => {
    const fetchSubscriptionDetails = async () => {
      try {
        const response = await userApi.getSubscriptionDetails();
        if (response?.data?.success) {
          setSubscription(response.data.data);
        }
      } catch (error) {
        handleError(error);
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptionDetails();
  }, []);

  useEffect(() => {
   
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setIsRedirecting(false);
      }
    };

    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  const handleManageSubscription = async () => {
    try {
      setIsRedirecting(true);
      const response = await userApi.createPortalSession();

      if (response?.data?.data?.url) {
        window.location.href = response.data.data.url;
      } else {
        console.error('No URL in response:', response);
        setIsRedirecting(false);
      }
    } catch (error) {
      handleError(error);
      setIsRedirecting(false);
    }
  };

  const getPermissionDisplay = (permission: any) => {
    if (permission.limit === null) {
      return "Allowed";
    } else if (permission.limit === -1) {
      return "Unlimited";
    } else if (permission.limit === 0) {
      return "Not Allowed";
    } else {
      return `${permission.used || 0} / ${permission.limit}`;
    }
  };

  const getPermissionName = (type: string) => {
    const names: Record<string, string> = {
      aims: "AIMS Access",
      headless: "API Tokens",
      pitches: "Pitches",
      roaster: "Roster",
    };
    return names[type] || type;
  };

  if (loading) {
    return (
      <div>
        <h2 className="mb-6 text-xl font-semibold tracking-tight text-black sm:text-2xl">
          Subscription Details
        </h2>
        <SettingsPanel className="mb-6">
          <div className="space-y-6 animate-pulse">
            <div className="w-1/3 h-8 bg-neutral-200 rounded"></div>
            <div className="w-1/4 h-6 bg-neutral-200 rounded"></div>
            <div className="w-1/2 h-6 bg-neutral-200 rounded"></div>
          </div>
        </SettingsPanel>
      </div>
    );
  }

  return (
    <div>
      <h2 className="mb-6 text-xl font-semibold tracking-tight text-black sm:text-2xl">
        Subscription Details
      </h2>

      {/* Subscription Info */}
      {subscription && (
        <SettingsPanel className="mb-6">
          <div className="space-y-6">
            {/* Plan */}
            <div>
              <p className="mb-1 text-xs font-medium tracking-wide uppercase text-neutral-500">Plan</p>
              <p className="text-xl font-bold text-black">
                {subscription.plan_name}
              </p>
            </div>

            {/* Status */}
            <div>
              <p className="mb-1 text-xs font-medium tracking-wide uppercase text-neutral-500">Status</p>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 border rounded-full bg-success-50 border-success-200">
                <div className="w-2 h-2 mr-2 bg-success-500 rounded-full"></div>
                <p className="text-xs font-medium capitalize text-success-800">
                  {subscription.status}
                </p>
              </div>
            </div>

            {/* Subscription Period */}
            <div>
              <p className="mb-3 text-xs font-medium tracking-wide uppercase text-neutral-500">Subscription Period</p>
              <div className="pl-4 space-y-2 border-l-2 border-neutral-200">
                <div className="flex items-baseline">
                  <span className="w-20 text-sm text-neutral-600">Started:</span>
                  <span className="text-sm font-medium text-black">
                    {format(new Date(subscription.current_period_start), "MMMM dd, yyyy")}
                  </span>
                </div>
                <div className="flex items-baseline">
                  <span className="w-20 text-sm text-neutral-600">Renews:</span>
                  <span className="text-sm font-medium text-black">
                    {format(new Date(subscription.current_period_end), "MMMM dd, yyyy")}
                  </span>
                </div>
              </div>
            </div>

            {/* Billing */}
            <div>
              <p className="mb-3 text-xs font-medium tracking-wide uppercase text-neutral-500">Billing</p>
              <div className="pl-4 border-l-2 border-neutral-200">
                <div className="flex items-baseline">
                  <span className="mr-2 text-sm text-neutral-600">Next Payment Date:</span>
                  <span className="text-sm font-medium text-black">
                    {format(new Date(subscription.current_period_end), "MMMM dd, yyyy")}
                  </span>
                </div>
              </div>
            </div>

            {/* Manage Subscription Button */}
            <div className="pt-4 border-t border-neutral-100">
              <Button
                onClick={handleManageSubscription}
                disabled={isRedirecting}
              >
                {isRedirecting ? "Redirecting..." : "Manage Subscription"}
              </Button>
            </div>
          </div>
        </SettingsPanel>
      )}

      {/* Permissions */}
      <SettingsPanel>
        <h3 className="mb-4 text-lg font-semibold text-black">
          Your Permissions
        </h3>
        <div className="space-y-3">
          {permissions.length > 0 ? (
            permissions.map((permission) => (
              <div
                key={permission.permission_id}
                className="flex items-start justify-between p-4 border rounded-lg bg-neutral-50 border-neutral-100"
              >
                <div className="flex-1">
                  <h4 className="font-medium text-black">
                    {getPermissionName(permission.type)}
                  </h4>
                  <p className="text-sm text-neutral-500">
                    Type: {permission.type}
                  </p>
                </div>
                <div className="ml-4 text-left">
                  <p className="font-semibold text-primary-700">
                    {getPermissionDisplay(permission)}
                  </p>
                </div>
              </div>
            ))
          ) : (
            <p className="py-4 text-neutral-500">
              No permissions found
            </p>
          )}
        </div>
      </SettingsPanel>
    </div>
  );
};

export default SubscriptionDetails;
